/**
 * Home — /  (Frac corporate marketing site, homepage)
 * Full i18n'd version.
 */

import React, {
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";
import {
  Radar,
  Sparkles,
  Zap,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Marquee } from "@/components/ui/Marquee";

import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { Counter } from "@/components/motion/Counter";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneA = React.lazy(() => import("@/components/scenes/SceneA"));
const SceneB = React.lazy(() => import("@/components/scenes/SceneB"));
const SceneC = React.lazy(() => import("@/components/scenes/SceneC"));
const SceneE = React.lazy(() => import("@/components/scenes/SceneE"));
const SceneF = React.lazy(() => import("@/components/scenes/SceneF"));

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Frac",
  legalName: "Frac Pte. Ltd.",
  url: "https://frac.io",
  logo: "https://frac.io/apple-touch-icon.png",
  foundingDate: "2022",
  description:
    "Frac is the real-world asset intelligence network — marketing technology, AI infrastructure, data tokenization and green compute for enterprises across APAC.",
  address: [
    {
      "@type": "PostalAddress",
      streetAddress: "8 Marina View, Asia Square Tower 1, #43-01",
      postalCode: "018960",
      addressLocality: "Singapore",
      addressCountry: "SG",
    },
    {
      "@type": "PostalAddress",
      streetAddress:
        "SO-21-1, Menara 1, Strata Office No.3, Jalan Bangsar, KL Eco City",
      postalCode: "59200",
      addressLocality: "Kuala Lumpur",
      addressCountry: "MY",
    },
  ],
  contactPoint: [
    { "@type": "ContactPoint", email: "hello@frac.io", contactType: "customer support" },
    { "@type": "ContactPoint", email: "partnerships@frac.io", contactType: "sales" },
    { "@type": "ContactPoint", email: "media@frac.io", contactType: "press" },
  ],
  founder: [
    { "@type": "Person", name: "Japhet Lim" },
    { "@type": "Person", name: "Melvin Tan" },
  ],
  sameAs: [
    "https://www.linkedin.com/company/frac-tional",
    "https://twitter.com/frac_tional",
    "https://www.youtube.com/@frac_tional",
  ],
};

const LAYER_HREFS = [
  "/platform/marketing",
  "/platform/ai",
  "/platform/data",
  "/platform/energy",
];

const MARKETING_ICONS = [Zap, Sparkles, Radar];

const RECOGNITION = [
  "MDEC (Malaysia Digital Status)",
  "MasChain Grant Winner",
  "VeChain Partner",
  "Crypto.com APAC Hackathon Top 12",
  "ByBit BGA",
  "XDC / Tenity Accelerator",
  "Hedera Web3 Accelerator Top 20",
  "Octopus Network Star Prize",
  "TRAXX",
  "GamBit Custody",
  "StraitsX",
  "Masverse",
  "Diamond Alpha",
  "RegalRare",
  "iGold",
  "Planet Spirits",
  "Quaich Bar",
  "Academic Labs",
  "Hinterland",
  "CAI Partners",
];

/* Helpers */
function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback({ aspectRatio = "16 / 9" }) {
  return (
    <div
      aria-hidden
      style={{
        width: "100%",
        aspectRatio,
        background: "var(--surface-base)",
      }}
    />
  );
}

function parseUnitParam(search) {
  const p = new URLSearchParams(search);
  const raw = p.get("unit");
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const i = Math.floor(n);
  if (i < 0 || i > 7) return null;
  return i;
}

/* §1 Hero */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("home");
  const locale = useLocale();

  return (
    <section
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="home-hero"
    >
      <div className="absolute inset-0" aria-hidden>
        <Suspense fallback={<SceneFallback aspectRatio="auto" />}>
          <SceneA
            scrollProgress={progress}
            style={{ position: "absolute", inset: 0 }}
          />
        </Suspense>
      </div>

      <div className="relative z-10 flex min-h-screen items-start pt-8 pb-8 md:items-center md:py-0">
        <div className="container-frac">
          <Reveal>
            <span className="eyebrow" data-testid="page-eyebrow">
              {t("hero.eyebrow")}
            </span>
          </Reveal>

          <MaskReveal
            as="h1"
            className="mt-4 max-w-5xl text-balance text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:mt-6 sm:text-5xl lg:text-6xl"
            data-testid="page-h1"
            lines={[
              t("hero.h1Line1"),
              <span key="l2">
                {t("hero.h1Line2Prefix")}
                <span
                  className="bg-gradient-brand bg-clip-text text-transparent"
                  data-testid="home-behaviour-gradient"
                >
                  {t("hero.h1Line2Highlight")}
                </span>
                {t("hero.h1Line2Suffix")}
              </span>,
            ]}
          />

          <Reveal delay={300}>
            <p className="mt-5 max-w-3xl text-base leading-normal text-ink-secondary sm:mt-8 sm:text-lg">
              {t("hero.sub")}
            </p>
          </Reveal>

          <Reveal delay={500}>
            <div className="mt-6 flex flex-wrap gap-3 sm:mt-10">
              <Button
                as={Link}
                to={addLocalePrefix("/contact", locale)}
                variant="primary-on-dark"
                size="lg"
                data-testid="hero-cta-primary"
              >
                {t("hero.ctaPrimary")}
              </Button>
              <Button
                as="a"
                href="https://nr9mx8metr.sg.larksuite.com/file/TTYAbVaNYoEyMhxsrteu01eMsHd?from=from_copylink"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary-on-dark"
                size="lg"
                aria-label={t("hero.ctaSecondaryAria")}
                data-testid="hero-cta-secondary"
              >
                {t("hero.ctaSecondary")}
                <ArrowUpRight
                  className="ml-1.5 h-4 w-4"
                  strokeWidth={1.75}
                  aria-hidden
                />
              </Button>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="scroll-cue" aria-hidden data-testid="hero-scroll-cue">
        <span>{t("hero.scrollCue")}</span>
      </div>
    </section>
  );
}

/* §2 Reframe */
function Reframe() {
  const { t } = useT("home");
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-reframe"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("reframe.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("reframe.h2")}
        </MaskReveal>

        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("reframe.p1")}
          </p>
        </Reveal>

        <Reveal delay={350}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("reframe.p2")}
          </p>
        </Reveal>

        <Reveal delay={550}>
          <p className="mt-12 text-2xl font-bold leading-snug text-ink-primary sm:text-3xl">
            {t("reframe.closing")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* §3 Stack */
function StackSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("home");
  const locale = useLocale();
  const layers = t("stack.layers", { returnObjects: true }) || [];

  return (
    <section
      ref={ref}
      className="container-frac py-24 md:py-32"
      data-testid="home-stack"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("stack.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("stack.h2")}
        </MaskReveal>
      </div>

      <div
        className="relative mt-12 h-[52vh] w-full overflow-hidden rounded-[16px] border border-hairline sm:h-[60vh]"
        data-testid="home-scene-b-wrapper"
      >
        <Suspense fallback={<SceneFallback />}>
          <SceneB
            scrollProgress={progress}
            style={{ position: "absolute", inset: 0 }}
          />
        </Suspense>
      </div>

      <Stagger className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" gap={70}>
        {layers.map((layer, i) => (
          <StaggerItem key={layer.number}>
            <Card
              variant="glass"
              interactive
              className="flex h-full flex-col"
              data-testid={`stack-card-${layer.number}`}
            >
              <div
                className="text-[61px] font-light leading-none text-frac-violet"
                style={{ fontWeight: 300 }}
              >
                {layer.number}
              </div>
              <h3 className="mt-4 text-xl font-bold text-ink-primary">
                {layer.name}
              </h3>
              <p className="mt-2 text-base italic text-ink-secondary">
                {layer.tagline}
              </p>
              <p className="mt-4 flex-1 text-sm leading-normal text-ink-secondary">
                {layer.description}
              </p>
              <Link
                to={addLocalePrefix(LAYER_HREFS[i], locale)}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-frac-violet transition-colors duration-fast hover:text-ink-primary"
                data-testid={`stack-card-${layer.number}-cta`}
              >
                {t("stack.exploreCta")}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

/* §4 Marketing */
function MarketingSection() {
  const { t } = useT("home");
  const locale = useLocale();
  const pillars = t("marketing.pillars", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-marketing"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("marketing.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("marketing.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("marketing.body")}
          </p>
        </Reveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3" gap={70}>
        {pillars.map((p, i) => {
          const Icon = MARKETING_ICONS[i];
          return (
            <StaggerItem key={i}>
              <Card variant="glass" className="h-full">
                <div
                  className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-md border border-hairline bg-surface-elevated/60"
                  aria-hidden
                >
                  {Icon && <Icon className="h-5 w-5 text-frac-violet" />}
                </div>
                <h3 className="text-base font-semibold text-ink-primary">
                  {p.name}
                </h3>
                <p className="mt-2 text-sm leading-normal text-ink-secondary">
                  {p.body}
                </p>
              </Card>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal delay={400}>
        <div className="mt-10">
          <Button
            as={Link}
            to={addLocalePrefix("/platform/marketing", locale)}
            variant="secondary-on-dark"
            size="md"
            data-testid="marketing-cta"
          >
            {t("marketing.cta")}
            <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
          </Button>
        </div>
      </Reveal>
    </section>
  );
}

/* §5 Privacy */
function PrivacyMembrane() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("home");
  const chips = t("privacy.chips", { returnObjects: true }) || [];

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="home-privacy"
    >
      <div className="relative grid grid-cols-1 md:grid-cols-2 md:min-h-[200vh]">
        <div className="md:sticky md:top-0 flex items-center py-16 md:h-screen">
          <div className="container-frac-narrow px-6 md:pl-16 md:pr-8">
            <Reveal>
              <span className="eyebrow">{t("privacy.eyebrow")}</span>
            </Reveal>
            <MaskReveal
              as="h2"
              className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
            >
              {t("privacy.h2")}
            </MaskReveal>

            <Reveal delay={200}>
              <p className="mt-8 text-lg leading-normal text-ink-secondary">
                {t("privacy.body")}
              </p>
            </Reveal>

            <div className="mt-8 flex flex-wrap gap-2" data-testid="privacy-chips">
              {chips.map((c, i) => (
                <Badge key={i} variant="violet" onDark>{c}</Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="md:sticky md:top-0 md:h-screen">
          <div
            className="relative h-[60vh] md:h-full w-full overflow-hidden border-l border-hairline"
            data-testid="home-scene-c-wrapper"
          >
            <Suspense fallback={<SceneFallback aspectRatio="auto" />}>
              <SceneC
                scrollProgress={progress}
                style={{ position: "absolute", inset: 0 }}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}

/* §6 AI */
function AIInfrastructure() {
  const { t } = useT("home");
  const locale = useLocale();
  const chips = t("ai.chips", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-ai"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("ai.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("ai.h2")}
        </MaskReveal>

        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("ai.body")}
          </p>
        </Reveal>

        <Reveal delay={300}>
          <p
            className="mt-5 border-l-2 border-frac-violet/60 pl-5 text-lg leading-normal text-ink-secondary"
            data-testid="home-ai-si-coda"
          >
            {t("ai.siCoda")}
          </p>
        </Reveal>
      </div>

      <Stagger
        className="mt-12 flex flex-wrap gap-3"
        gap={100}
        data-testid="ai-chips"
      >
        {chips.map((chip, i) => (
          <StaggerItem key={i}>
            <span
              className="inline-flex items-center rounded-full border border-hairline bg-surface-elevated/60 px-5 py-3 text-[20px] font-semibold text-ink-primary transition-all duration-base hover:border-active-border hover:shadow-elev-md hover:[background:linear-gradient(120deg,rgba(114,0,219,0.14),rgba(42,10,74,0.14))]"
              data-testid={`ai-chip-${i}`}
            >
              {chip}
            </span>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal delay={400}>
        <div className="mt-10">
          <Button
            as={Link}
            to={addLocalePrefix("/platform/ai", locale)}
            variant="secondary-on-dark"
            size="md"
            data-testid="ai-cta"
          >
            {t("ai.cta")}
            <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
          </Button>
        </div>
      </Reveal>
    </section>
  );
}

/* §7 Why Tokenize */
function WhyTokenize() {
  const ref = useRef(null);
  const location = useLocation();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("home");
  const locale = useLocale();
  const outcomes = t("why.outcomes", { returnObjects: true }) || [];

  const initialUnit = useMemo(() => {
    const parsed = parseUnitParam(location.search);
    return parsed ?? 3;
  }, [location.search]);

  const handleUnitHighlight = useCallback((n) => {
    const params = new URLSearchParams(window.location.search);
    params.set("unit", String(n));
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, []);

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="home-why"
    >
      <div className="relative min-h-[220vh]">
        <div className="sticky top-0 flex min-h-screen flex-col items-center justify-center py-16">
          <div className="container-frac">
            <div className="mx-auto max-w-4xl text-center">
              <Reveal>
                <span className="eyebrow">{t("why.eyebrow")}</span>
              </Reveal>
              <MaskReveal
                as="h2"
                className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
              >
                {t("why.h2")}
              </MaskReveal>
              <Reveal delay={200}>
                <p
                  className="mt-8 text-4xl font-light leading-tight text-ink-primary sm:text-5xl"
                  style={{ fontWeight: 300 }}
                >
                  {t("why.answer")}
                </p>
              </Reveal>
              <Reveal delay={350}>
                <p className="mx-auto mt-8 max-w-3xl text-base leading-normal text-ink-secondary sm:text-lg">
                  {t("why.body")}
                </p>
              </Reveal>
            </div>

            <div
              className="relative mx-auto mt-10 h-[46vh] w-full max-w-6xl overflow-hidden rounded-[16px] border border-hairline"
              data-testid="home-scene-e-wrapper"
            >
              <Suspense fallback={<SceneFallback />}>
                <SceneE
                  scrollProgress={progress}
                  defaultUnit={initialUnit}
                  onUnitHighlight={handleUnitHighlight}
                  style={{ position: "absolute", inset: 0 }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      </div>

      <div className="container-frac py-16">
        <Stagger className="grid grid-cols-1 gap-4 md:grid-cols-3" gap={70}>
          {outcomes.map((o, i) => (
            <StaggerItem key={i}>
              <Card variant="glass" className="h-full">
                <h3 className="text-lg font-bold text-ink-primary">{o.name}</h3>
                <p className="mt-3 text-sm leading-normal text-ink-secondary">
                  {o.body}
                </p>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={400}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/platform/data", locale)}
              variant="secondary-on-dark"
              size="md"
              data-testid="why-cta"
            >
              {t("why.cta")}
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* §8 Energy */
function EnergySection() {
  const { t } = useT("home");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-energy"
    >
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[3fr_2fr] lg:gap-16">
        <div>
          <Reveal>
            <span className="eyebrow">{t("energy.eyebrow")}</span>
          </Reveal>
          <Reveal delay={100}>
            <div className="mt-4">
              <Badge status="ROADMAP" onDark data-testid="energy-roadmap-badge">
                {t("energy.badge")}
              </Badge>
            </div>
          </Reveal>
          <MaskReveal
            as="h2"
            className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
          >
            {t("energy.h2")}
          </MaskReveal>
          <Reveal delay={200}>
            <p className="mt-8 max-w-2xl text-lg leading-normal text-ink-secondary">
              {t("energy.body")}
            </p>
          </Reveal>
          <Reveal delay={350}>
            <p className="mt-6 max-w-2xl text-sm italic text-ink-tertiary">
              {t("energy.caveat")}
            </p>
          </Reveal>
          <Reveal delay={450}>
            <div className="mt-10">
              <Button
                as={Link}
                to={addLocalePrefix("/platform/energy", locale)}
                variant="secondary-on-dark"
                size="md"
                data-testid="energy-cta"
              >
                {t("energy.cta")}
                <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
              </Button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={200}>
          <div
            className="relative w-full overflow-hidden rounded-[16px] border border-hairline"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="energy-illustration"
          >
            <Suspense
              fallback={
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "var(--surface-raised)",
                  }}
                />
              }
            >
              <SceneF
                variant="teaser"
                style={{ position: "absolute", inset: 0 }}
              />
            </Suspense>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* §9 Rails */
function RailsSection() {
  const { t } = useT("home");
  const locale = useLocale();
  const stats = t("rails.stats", { returnObjects: true }) || [];
  const STAT_META = [
    { to: 400, format: (n) => `$${Math.round(n)}M+`, testId: "stat-value" },
    { to: 8, format: (n) => `${Math.round(n)}`, testId: "stat-deals" },
    { to: 3, format: (n) => `${Math.round(n)}`, testId: "stat-chains" },
    { to: 30, format: (n) => `${Math.round(n)} days`, testId: "stat-deployment" },
  ];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-rails"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("rails.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("rails.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("rails.body")}
          </p>
        </Reveal>
      </div>

      <Stagger className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" gap={70}>
        {STAT_META.map((s, i) => (
          <StaggerItem key={s.testId}>
            <Card variant="glass" className="h-full">
              <div
                className="text-4xl font-bold leading-none text-ink-primary sm:text-5xl"
                data-testid={s.testId}
              >
                <Counter to={s.to} format={s.format} duration={1200} />
              </div>
              <p className="mt-4 text-sm leading-normal text-ink-secondary">
                {stats[i]?.label}
              </p>
            </Card>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal delay={400}>
        <div className="mt-10">
          <Button
            as={Link}
            to={addLocalePrefix("/platform/tokenization", locale)}
            variant="secondary-on-dark"
            size="md"
            data-testid="rails-cta"
          >
            {t("rails.cta")}
            <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
          </Button>
        </div>
      </Reveal>
    </section>
  );
}

/* §10 Recognition */
function RecognitionSection() {
  const { t } = useT("home");
  return (
    <section
      className="py-24 md:py-32"
      data-testid="home-recognition"
    >
      <div className="container-frac">
        <div className="max-w-3xl">
          <Reveal>
            <span className="eyebrow">{t("recognition.eyebrow")}</span>
          </Reveal>
          <MaskReveal
            as="h2"
            className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
          >
            {t("recognition.h2")}
          </MaskReveal>
        </div>
      </div>

      <div className="mt-12">
        <Marquee
          duration={40}
          ariaLabel={t("recognition.marqueeAria")}
          testIdBase="recognition-marquee"
          items={RECOGNITION.map((name) => name)}
          itemClassName="inline-flex items-center rounded-full border border-hairline bg-surface-elevated/40 px-5 py-2.5 text-sm font-semibold text-ink-secondary"
        />
      </div>
    </section>
  );
}

/* §11 Final CTA */
function FinalCta() {
  const { t } = useT("home");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="home-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="mx-auto text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
          lines={[t("finalCta.h2Line1"), t("finalCta.h2Line2")]}
        />
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("finalCta.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useT("home");
  return (
    <>
      <PageSeo
        title={t("seoTitle")}
        description={t("seoDescription")}
        path="/"
        image="/og/home.png"
      >
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </PageSeo>

      <div data-testid="page-home">
        <Hero />
        <Reframe />
        <StackSection />
        <MarketingSection />
        <PrivacyMembrane />
        <AIInfrastructure />
        <WhyTokenize />
        <EnergySection />
        <RailsSection />
        <RecognitionSection />
        <FinalCta />
      </div>
    </>
  );
}
